// One imperative write PER ROW — `lib/mutation.ts`'s keyed sibling, and the ONE
// implementation of a per-row in-flight lock. `createMutation` is single-flight
// per SURFACE, which is exactly what a row-level control must not be: one lock
// for the whole list lets row A's write silently drop row B's.
//
// It is here and not in `packages/kit` because it calls `createStore`. Kit
// imports no framework, and that is WHY every workspace may import it; a Solid
// primitive belongs beside its consumers. `packages/ui` becomes the answer on a
// second app, not before.
//
// SEVEN sites had hand-rolled this same ladder — guard, set-before-await,
// release in `finally` — against a `createStore<Record<string, boolean>>({})`:
// `artifact-actions`, `donationInbox`, `grant-actions`, `repoToggle`,
// `webhookTest`, `routes/Donations` (detach) and `routes/Blockers`. Two of them
// (`repoToggle`, `webhookTest`) split the check across two functions — a `run`
// that READ the flag and a `flip`/`send` that WROTE it — so the guard could be
// forgotten at a third call site without either half looking wrong. Here it is
// unconditional and there is no second entry point.
//
// The call is passed PER INVOCATION, not per construction, and that is the
// difference from `createMutation`. Four of the seven run more than one act
// against the same key (share/delete/download an artifact, answer/withdraw a
// blocker, decline/choose a gift), and those acts must share ONE busy key: a
// mutation-per-act would give each its own store and let a reader delete the
// row a share is still writing.

import { createSignal } from 'solid-js';
import { createStore } from 'solid-js/store';
import { errorReason } from './api-errors';

/** Per-row write state for one surface. */
export type KeyedMutation = {
  /** True while THIS key has a call in flight. Reactive — read it in JSX. */
  readonly isBusy: (key: string) => boolean;
  /** Failure copy from the last attempt, or null. `errorReason`, so a 422 `detail` wins. */
  readonly error: () => string | null;
  /** Clear the failure notice — wire to a `<Toast/>`'s `onDismiss`. */
  readonly clearError: () => void;
  /**
   * Fire the write for one key. Awaitable (so a test can settle it) and safe to
   * drop on the floor — it resolves on failure too. A call made while THIS key
   * is in flight is DROPPED, not queued; another key is unaffected.
   *
   * `fallback` overrides the surface's default for one act. Not a knob: two of
   * the seven already carried per-act copy (`donations.decline_error` vs
   * `donations.model_error`), and the alternative is a mutation per act, which
   * forfeits the shared busy key above.
   */
  readonly run: (key: string, call: () => Promise<void>, fallback?: string) => Promise<void>;
};

export type KeyedMutationInput = {
  /**
   * Shown when the failure carries no `detail` of its own. Pass a `t('…')`,
   * never a literal: this string reaches the screen.
   */
  readonly fallback: string;
  /**
   * Ran with the same string `error()` takes, for a surface that reports through
   * a `<Toast/>` it already owns (`repoToggle`, `webhookTest`) rather than
   * rendering `error()` itself. Both happen, so no caller syncs one to the other
   * through a `createEffect`.
   */
  readonly onError?: (message: string, cause: unknown) => void;
  /**
   * Ran once the guard has PASSED, before the first `await` — where a surface
   * clears the extra state a fresh attempt invalidates (`grant-actions` and
   * `Blockers` both hold a benign-outcome `notice` beside the error). Inside the
   * guard deliberately: a dropped re-entrant call must not wipe the notice the
   * call still in flight produced.
   */
  readonly onStart?: () => void;
};

export function createKeyedMutation(input: KeyedMutationInput): KeyedMutation {
  const [busy, setBusy] = createStore<Record<string, boolean>>({});
  const [error, setError] = createSignal<string | null>(null);

  // The LOCK is this set, not the `busy` store, and deliberately: it is written
  // synchronously before the first `await`, while a store write is deferred
  // inside `batch`. `createMutation` makes the same choice for the same reason.
  // `busy` is the RENDERING of the lock and exists only to be reactive.
  const inFlight = new Set<string>();

  const run = async (key: string, call: () => Promise<void>, fallback?: string): Promise<void> => {
    if (inFlight.has(key)) return;
    inFlight.add(key);
    setBusy(key, true);
    setError(null);
    input.onStart?.();
    try {
      await call();
    } catch (err) {
      const message = errorReason(err, fallback ?? input.fallback);
      setError(message);
      input.onError?.(message, err);
    } finally {
      inFlight.delete(key);
      setBusy(key, false);
    }
  };

  return {
    isBusy: (key) => busy[key] === true,
    error,
    clearError: () => setError(null),
    run,
  };
}
